import React, { useCallback } from 'react';
import { Button, Tooltip } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import toast from 'react-hot-toast';
import useScreenRecorder, { isScreenRecordingSupported } from '../../hooks/useScreenRecorder';
import { useScreenRecorderPlacement } from './ScreenRecorderPlacement';
import { leadAssignmentEnabled } from '../../config/featureFlags';

/**
 * Header control that starts a tab-only recording.
 * Only rendered when REACT_APP_FEATURE_LEAD_ASSIGNMENT is enabled.
 * While recording/uploading, the app-level ScreenRecorderOverlay shows the
 * timer, Stop button and upload progress on every screen, so this renders nothing.
 */
const ScreenRecorderButton = ({ publicSession = false, publicLinkToken = null, inlineControls = false }) => {
  const { status, start } = useScreenRecorder();
  const { setContainer } = useScreenRecorderPlacement();

  const handleStart = useCallback(async () => {
    try {
      await start({ publicSession, publicLinkToken: publicSession ? publicLinkToken : null });
      toast.success('Recording started with microphone audio. Only this tab is being captured.');
    } catch (e) {
      if (e?.message && !/cancelled/i.test(e.message)) toast.error(e.message);
    }
  }, [start, publicSession, publicLinkToken]);

  if (!leadAssignmentEnabled) return null;
  if (!isScreenRecordingSupported()) return null;
  if (status === 'recording') {
    return inlineControls ? <div ref={setContainer} /> : null;
  }

  return (
    <Tooltip title="Record this app tab with microphone audio. Allow microphone access and enable tab audio in the sharing dialog to include it.">
      <Button
        size="small"
        variant="outlined"
        startIcon={<FiberManualRecordIcon sx={{ color: '#E31E25' }} />}
        onClick={handleStart}
        disabled={status === 'starting'}
        sx={{ mr: 3, textTransform: 'none', borderColor: 'var(--primary-color)', color: 'var(--primary-color)' }}
      >
        {status === 'starting' ? 'Starting…' : 'Record'}
      </Button>
    </Tooltip>
  );
};

export default ScreenRecorderButton;
